import { Button } from "@/components/ui/button";
import { NavigationArrow, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { pickNavigationTarget, isUsableAddress, openGoogleMaps, openWaze } from "@/lib/navigation";

/**
 * Reusable driver navigation buttons — Google Maps + Waze.
 *
 * Picks the right destination automatically based on the ride's status:
 *   • status ∈ {assigned, er, sp, available, interested, …}  → pickup address
 *   • status ∈ {pec, dep, completed}                          → dropoff address
 *
 * Designed for re-use across:
 *   - S.DRIVE driver rides (today)
 *   - Driver personal rides (next phase)
 *
 * Disables both buttons cleanly when no usable address is available, so the
 * card never crashes on partial / legacy ride documents.
 */
export default function DriverNavButtons({ ride, size = "default", testidPrefix = "nav", className = "" }) {
  const { target, phase, pickup, dropoff } = pickNavigationTarget(ride);
  const usable = isUsableAddress(target);

  // The visible "going to" caption helps the driver confirm what address will
  // be opened before they tap. Tiny but high-trust UX.
  const caption = phase === "dropoff" ? "Vers l'arrivée" : "Vers le départ";

  const handle = (open) => () => {
    if (!usable) {
      toast.error("Adresse indisponible pour cette course");
      return;
    }
    open(target);
  };

  const heightCls = size === "compact" ? "h-9 text-xs" : "h-10 text-sm";

  return (
    <div className={`flex flex-col gap-1 ${className}`} data-testid={`${testidPrefix}-block`}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <MapPin size={11} className="text-zinc-600" />
        {caption}
        <span className="text-zinc-600 truncate">· {target || "(adresse manquante)"}</span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button
          data-testid={`${testidPrefix}-gmaps`}
          onClick={handle(openGoogleMaps)}
          disabled={!usable}
          variant="outline"
          className={`border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40 disabled:hover:bg-transparent ${heightCls}`}
          title={usable ? `Google Maps → ${target}` : "Adresse manquante"}
        >
          <NavigationArrow size={size === "compact" ? 14 : 16} className="mr-1.5" />
          Google Maps
        </Button>
        <Button
          data-testid={`${testidPrefix}-waze`}
          onClick={handle(openWaze)}
          disabled={!usable}
          variant="outline"
          className={`border-violet-500/40 text-violet-300 hover:bg-violet-500/10 disabled:opacity-40 disabled:hover:bg-transparent ${heightCls}`}
          title={usable ? `Waze → ${target}` : "Adresse manquante"}
        >
          <NavigationArrow size={size === "compact" ? 14 : 16} className="mr-1.5" />
          Waze
        </Button>
      </div>
    </div>
  );
}
